import { motion } from "motion/react"
import { useState } from "react"
import { useLocation } from "react-router"

import { parseNumber } from "@forento/core/utilities/number"
import { getPasswordValidationError, passwordRequirementsText } from "@forento/core/utilities/password"
import Form from "@forento/ui/components/Form"

import Button, { SubmitButton } from "~/components/Button"
import InputField from "~/components/InputField"
import Link from "~/components/Link"
import LoadingIndicator from "~/components/LoadingIndicator"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import Layout, { Logo } from "./Layout"
import { authCardClass, staggerContainer, staggerItem } from "./Layout/authCard"

const NewPasswordPage: React.FC = () => {
	const params = new URLSearchParams(useLocation().search)

	const [isSubmitting, setSubmitting] = useState(false)
	const [isPasswordReset, setPasswordReset] = useState(false)
	const [password, setPassword] = useState("")
	const [passwordConfirmation, setPasswordConfirmation] = useState("")
	const [error, setError] = useState<string>()

	const userId = parseNumber(params.get("user") ?? "") ?? -1
	const token = params.get("token") ?? ""

	const tokenCheck = query.user.checkResetPasswordToken.useQuery({ userId, token })

	const submitNewPassword = async () => {
		if (tokenCheck.data?.status !== "success") return
		if (password.length === 0 || passwordConfirmation.length === 0) {
			setError("Please fill in all fields.")
			return
		}
		if (password !== passwordConfirmation) {
			setError("Passwords don't match.")
			return
		}
		const passwordError = getPasswordValidationError(password)
		if (passwordError !== null) {
			setError(passwordError)
			return
		}

		setSubmitting(true)
		setError(undefined)
		try {
			await trpc.user.setPasswordFromToken.mutate({ userId, token, newPassword: password })
			setPasswordReset(true)
		} catch (error) {
			console.error(error)
			setSubmitting(false)
			return Promise.reject(error)
		}
		setSubmitting(false)
	}

	return (
		<Layout>
			<Logo />
			<div className={authCardClass}>
				{tokenCheck.error ? (
					<motion.div
						className="flex w-full flex-col items-stretch gap-0"
						variants={staggerContainer}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={staggerItem}>
							<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
								Set new password
							</h1>
							<p className="mt-1.5 text-left text-sm text-zinc-500">
								An error occurred while resetting the password. Please try again later.
							</p>
						</motion.div>
					</motion.div>
				) : !tokenCheck.data ? (
					<div className="flex flex-col items-center gap-4 py-8">
						<LoadingIndicator />
						<p className="text-sm text-zinc-500">Checking reset link...</p>
					</div>
				) : tokenCheck.data.status === "invalid" ? (
					<motion.div
						className="flex w-full flex-col items-stretch gap-0"
						variants={staggerContainer}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={staggerItem}>
							<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">Invalid link</h1>
							<p className="mt-1.5 text-left text-sm text-zinc-500">
								Invalid password reset link. It may be expired or already used.
							</p>
						</motion.div>
						<motion.div variants={staggerItem} className="mt-6">
							<Link
								to={routes.account.resetPassword()}
								className="text-primary inline-flex cursor-pointer text-sm font-medium hover:underline"
							>
								Request a new link
							</Link>
						</motion.div>
					</motion.div>
				) : isPasswordReset ? (
					<motion.div
						className="flex w-full flex-col items-stretch gap-0"
						variants={staggerContainer}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={staggerItem}>
							<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
								Password updated
							</h1>
							<p className="mt-1.5 text-left text-sm text-zinc-500">
								Your password is now updated. You can sign in with your new password.
							</p>
						</motion.div>
						<motion.div variants={staggerItem} className="mt-6">
							<Button
								variant="primary"
								onClick={routes.account.signin()}
								className="w-full cursor-pointer rounded-xl py-3 text-sm font-semibold"
							>
								Sign in
							</Button>
						</motion.div>
					</motion.div>
				) : (
					<Form onSubmit={submitNewPassword}>
						<motion.div
							className="flex w-full flex-col items-stretch gap-0"
							variants={staggerContainer}
							initial="hidden"
							animate="visible"
						>
							<motion.div variants={staggerItem}>
								<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
									Set new password
								</h1>
								<p className="mt-1.5 text-left text-sm text-zinc-500">
									Choose a new password for your account.
								</p>
							</motion.div>

							{error !== undefined && (
								<motion.p
									variants={staggerItem}
									className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700"
								>
									{error}
								</motion.p>
							)}

							<motion.div variants={staggerItem} className="mt-6">
								<InputField
									inputType="password"
									label="New password"
									value={password}
									onChange={setPassword}
									autoComplete="new-password"
								/>
								<p className="mt-2 text-left text-xs leading-relaxed text-zinc-500">
									{passwordRequirementsText}
								</p>
							</motion.div>

							<motion.div variants={staggerItem} className="mt-4">
								<InputField
									inputType="password"
									label="Confirm new password"
									value={passwordConfirmation}
									onChange={setPasswordConfirmation}
									autoComplete="new-password"
								/>
							</motion.div>

							<motion.div variants={staggerItem} className="mt-5">
								<SubmitButton
									variant="primary"
									isLoading={isSubmitting}
									className="w-full cursor-pointer rounded-xl py-3 text-sm font-semibold"
								>
									Reset password
								</SubmitButton>
							</motion.div>
						</motion.div>
					</Form>
				)}
			</div>
		</Layout>
	)
}

export default NewPasswordPage
